import useUserStore from '@/stores/useUserStore';
import anewSignIn from './anewSignIn';
import { readCookie, setDomainCookie } from './cookie';
import getAppClient, { AppClientValue } from './getAppClient';
import getDomainPlatform, { DomainPlatformValue } from './getDomainPlatform';
import Logger from './Logger';

export interface Subject {
  id: string;
  name: string;
}

const SUBJECT_KEY = 'subject';

// 思政专用学科，FS 平台不使用
const IPE_SUBJECT_ID = '100001';

/**
 * FS 平台没有「登录时选定」的站点学科，但保留自己的外壳：外壳各处统一以账号当前学科
 * （服务端教材档案的 is_current 行，经 useUserStore.currentSubject 提供）作为站点学科的等价物。
 * 仍不读写 FS 源 localStorage 的 subject 键与根域 cookie（根域 cookie 与宿主、其他平台共用；
 * 当前学科随 user_store 持久化在 FS 源下，不写根域）。
 */
function isSiteSubjectDisabled() {
  return getDomainPlatform() === DomainPlatformValue.Fs;
}

function loadSubject(): Subject | null {
  if (isSiteSubjectDisabled()) {
    const current = useUserStore.getState().currentSubject;
    return current?.id ? current : null;
  }

  const raw = localStorage.getItem(SUBJECT_KEY) ?? readCookie(SUBJECT_KEY);
  if (!raw) return null;
  try {
    const s = JSON.parse(raw) as Subject;
    localStorage.setItem(SUBJECT_KEY, JSON.stringify(s));
    return s;
  } catch (e) {
    Logger.error(e);
    return null;
  }
}

export function hasSubject() {
  return !!loadSubject();
}

export function getSubject(): Subject {
  const s = loadSubject();
  if (s) return s;

  // FS 还没有当前学科属于正常状态（新账号尚未在数智化课件选过学科），不重新登录；
  // 调用方应处理空学科，留日志便于发现遗漏
  if (isSiteSubjectDisabled()) {
    Logger.debug('FS 平台还没有当前学科，getSubject() 返回空学科');
    return { id: '', name: '' };
  }

  if (getAppClient() !== AppClientValue.Student) {
    anewSignIn('没有选择学科，请重新登录');
  }

  return { id: '', name: '' };
}

export function setSubject(subject: Subject) {
  if (isSiteSubjectDisabled()) return;

  const val = JSON.stringify(subject);
  localStorage.setItem(SUBJECT_KEY, val);
  setDomainCookie(SUBJECT_KEY, val);
}

export function syncSubjectToCookie() {
  // FS 不往根域写学科：cookie 与宿主、其他平台共用，写回去会重新耦合两边（需求 13）
  if (isSiteSubjectDisabled()) return;

  const s = loadSubject();
  if (s) {
    setDomainCookie(SUBJECT_KEY, JSON.stringify(s));
  }
}

/**
 * 仅用于 FS 老用户迁移：读取改造前登录时留在本浏览器的学科（只读 localStorage，不读根域 cookie）。
 * 服务端还没有「当前所在学科」时，用它决定首次进入数智化课件的落点。
 */
export function readLegacyFsSubject(): Subject | null {
  const raw = localStorage.getItem(SUBJECT_KEY);
  if (!raw) return null;
  try {
    const s = JSON.parse(raw) as Partial<Subject> | null;
    if (!s?.id || !s.name || String(s.id) === IPE_SUBJECT_ID) return null;
    return { id: String(s.id), name: s.name };
  } catch (e) {
    Logger.error(e);
    return null;
  }
}

/**
 * FS 已有服务端记录的当前学科后，浏览器里的旧学科不再参与判断，清除它。
 */
export function clearLegacyFsSubject() {
  localStorage.removeItem(SUBJECT_KEY);
}
